function _findPageDots(root) {
  if (!root) return null;
  const pd = root.querySelector('.product-gallery__thumbnail-list');
  if (pd) return pd;
  return Array.from(root.querySelectorAll('*')).find(el => el.classList && el.classList.contains('product-gallery__thumbnail-list')) || null;
}

const MAX_THUMBNAILS_FOR_HEIGHT = 8;
const THUMBNAIL_NAV_STEP = 8;

function getAllItems(pageDots) {
  if (!pageDots) return [];
  return pageDots.items ?? Array.from(pageDots.children);
}

function getThumbnailButtons(pageDots) {
  const items = getAllItems(pageDots);
  const thumbs = items.filter(el => el && el.classList && el.classList.contains('product-gallery__thumbnail'));
  return thumbs.length ? thumbs : items;
}

function getThumbnailsScrollStep(pageDots) {
  if (!pageDots) return 0;
  const thumbs = Array.from(pageDots.querySelectorAll('.product-gallery__thumbnail'))
    .filter(el => !el.hidden && el.offsetParent !== null)
    .slice(0, MAX_THUMBNAILS_FOR_HEIGHT);
  if (!thumbs.length) return 0;

  const totalHeight = thumbs.reduce((acc, thumb) => {
    const styles = window.getComputedStyle(thumb);
    const marginTop = parseFloat(styles.marginTop) || 0;
    const marginBottom = parseFloat(styles.marginBottom) || 0;
    return acc + thumb.getBoundingClientRect().height + marginTop + marginBottom;
  }, 0);

  const listStyles = window.getComputedStyle(pageDots);
  const rowGap = parseFloat(listStyles.rowGap || listStyles.gap) || 0;
  const gapTotal = rowGap * Math.max(thumbs.length - 1, 0);

  return Math.ceil(totalHeight + gapTotal);
}

function getThumbnailWrapper(root) {
  if (!root) return null;
  return root.matches?.('.product-gallery__thumbnail-list-wrapper')
    ? root
    : (root.querySelector?.('scroll-shadow.product-gallery__thumbnail-list-wrapper')
      || root.querySelector?.('.product-gallery__thumbnail-list-wrapper'));
}

function updateThumbnailListMaxHeight(root) {
  const thumbnailWrapper = getThumbnailWrapper(root);
  const upBtn = root?.querySelector?.('.product-gallery__thumbnail-nav--up');
  const downBtn = root?.querySelector?.('.product-gallery__thumbnail-nav--down');

  const pageDots = _findPageDots(root);
  if (!pageDots || !thumbnailWrapper) return;

  const visibleCount = getThumbnailButtons(pageDots)
    .filter(el => !!el && !el.hidden && el.offsetParent !== null).length;
  const cachedCount = parseInt(thumbnailWrapper.dataset.fixedVisibleCount || '0', 10);
  const isFirstCalc = cachedCount === 0;

  if (visibleCount !== cachedCount) {
    delete thumbnailWrapper.dataset.fixedWrapperHeight;
    delete thumbnailWrapper.dataset.fixedPageDotsMaxHeight;
    pageDots.scrollTop = 0;
  }

  if (!isFirstCalc && !thumbnailWrapper.dataset.transitionApplied) {
    thumbnailWrapper.style.transition = 'max-height 0.25s ease, height 0.25s ease';
    pageDots.style.transition = 'max-height 0.25s ease';
    thumbnailWrapper.dataset.transitionApplied = 'true';
  }

  const fixedWrapperHeight = parseFloat(thumbnailWrapper.dataset.fixedWrapperHeight || '');
  const fixedPageDotsMaxHeight = parseFloat(thumbnailWrapper.dataset.fixedPageDotsMaxHeight || '');
  if (Number.isFinite(fixedWrapperHeight) && Number.isFinite(fixedPageDotsMaxHeight)) {
    thumbnailWrapper.style.maxHeight = `${fixedWrapperHeight}px`;
    thumbnailWrapper.style.height = `${fixedWrapperHeight}px`;
    pageDots.style.maxHeight = `${fixedPageDotsMaxHeight}px`;
    return;
  }

  const thumbs = getThumbnailButtons(pageDots)
    .filter(el => !!el && !el.hidden && el.offsetParent !== null)
    .slice(0, MAX_THUMBNAILS_FOR_HEIGHT);

  if (!thumbs.length) {
    thumbnailWrapper.style.removeProperty('max-height');
    pageDots.style.removeProperty('max-height');
    return;
  }

  let minTop = Infinity;
  let maxBottom = -Infinity;
  let totalHeightSum = 0;
  for (const thumb of thumbs) {
    const styles = window.getComputedStyle(thumb);
    const marginTop = parseFloat(styles.marginTop) || 0;
    const marginBottom = parseFloat(styles.marginBottom) || 0;
    const rect = thumb.getBoundingClientRect();
    minTop = Math.min(minTop, rect.top - marginTop);
    maxBottom = Math.max(maxBottom, rect.bottom + marginBottom);
    totalHeightSum += rect.height + marginTop + marginBottom;
  }
  const boundingHeight = Math.max(maxBottom - minTop, 0);

  const isHorizontalOrGrid = totalHeightSum > boundingHeight + 4;
  const totalHeight = isHorizontalOrGrid ? boundingHeight : totalHeightSum;

  const listStyles = window.getComputedStyle(pageDots);
  const rowGap = parseFloat(listStyles.rowGap || listStyles.gap) || 0;
  const gapTotal = isHorizontalOrGrid ? 0 : rowGap * Math.max(thumbs.length - 1, 0);
  const navButtonsHeight = [upBtn, downBtn].reduce((acc, btn) => {
    if (!btn || btn.offsetParent === null) return acc;

    const styles = window.getComputedStyle(btn);
    const marginTop = parseFloat(styles.marginTop) || 0;
    const marginBottom = parseFloat(styles.marginBottom) || 0;

    return acc + btn.getBoundingClientRect().height + marginTop + marginBottom;
  }, 0);
  const maxHeight = Math.ceil(totalHeight + gapTotal + navButtonsHeight);
  const wrapperStyles = window.getComputedStyle(thumbnailWrapper);
  const wrapperGap = parseFloat(wrapperStyles.rowGap || wrapperStyles.gap) || 0;
  const wrapperMaxHeight = Math.ceil(maxHeight + (wrapperGap * 2));

  const currentVisibleCount = getThumbnailButtons(pageDots)
    .filter(el => !!el && !el.hidden && el.offsetParent !== null).length;
  thumbnailWrapper.dataset.fixedVisibleCount = String(currentVisibleCount);
  thumbnailWrapper.dataset.fixedWrapperHeight = String(wrapperMaxHeight);
  thumbnailWrapper.dataset.fixedPageDotsMaxHeight = String(Math.ceil(totalHeight));

  thumbnailWrapper.style.maxHeight = `${wrapperMaxHeight}px`;
  thumbnailWrapper.style.height = `${wrapperMaxHeight}px`;
  pageDots.style.maxHeight = `${totalHeight}px`;
}

function updateThumbNavState(root) {
  const pageDots = _findPageDots(root);
  const upBtn = root.querySelector?.('.product-gallery__thumbnail-nav--up');
  const downBtn = root.querySelector?.('.product-gallery__thumbnail-nav--down');
  if (!pageDots || !upBtn || !downBtn) return;

  const thumbnailWrapper = getThumbnailWrapper(root);
  const targetMaxHeight = parseFloat(thumbnailWrapper?.dataset.fixedPageDotsMaxHeight || '');
  const effectiveClientHeight = Number.isFinite(targetMaxHeight) ? targetMaxHeight : pageDots.clientHeight;

  const items = pageDots.items ?? Array.from(pageDots.children);
  const visible = items.filter(el => !!el && el.offsetParent !== null && !el.hidden);
  const scrollTop = pageDots.scrollTop || 0;
  const canUp = scrollTop > 0;
  const hasOverflow = visible.length > MAX_THUMBNAILS_FOR_HEIGHT && pageDots.scrollHeight > effectiveClientHeight + 1;
  const atScrollBottom = !hasOverflow || (pageDots.scrollHeight - scrollTop - effectiveClientHeight <= 1);
  const canDown = !atScrollBottom;
  const shouldShowDown = canDown && hasOverflow;

  upBtn.disabled = !canUp;
  downBtn.disabled = !canDown;
  upBtn.setAttribute('aria-disabled', String(!canUp));
  downBtn.setAttribute('aria-disabled', String(!canDown));
  upBtn.classList.toggle('is-disabled', !canUp);
  downBtn.classList.toggle('is-disabled', !canDown);
  upBtn.hidden = !canUp;
  downBtn.hidden = !shouldShowDown;
}

function setupGalleryThumbnailNavSimple(wrapper) {
  if (!wrapper) return;

  const pageDots = _findPageDots(wrapper);
  const upBtn = wrapper.querySelector('.product-gallery__thumbnail-nav--up');
  const downBtn = wrapper.querySelector('.product-gallery__thumbnail-nav--down');
  if (!pageDots || !upBtn || !downBtn) return;

  if (!pageDots.dataset.galleryThumbScrollBound) {
    pageDots.dataset.galleryThumbScrollBound = 'true';
    pageDots.addEventListener('scroll', () => updateThumbNavState(wrapper), { passive: true });
  }

  updateThumbnailListMaxHeight(wrapper);
  updateThumbNavState(wrapper);
}

function initAllGalleriesSimple() {
  const wrappers = Array.from(document.querySelectorAll('.product-gallery__thumbnail-list-wrapper--list'));
  wrappers.forEach(w => {
    setupGalleryThumbnailNavSimple(w);
    updateThumbnailListMaxHeight(w);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllGalleriesSimple);
} else {
  initAllGalleriesSimple();
}

if (typeof MutationObserver !== 'undefined' && !window._galleryGlobalMutationObserver) {
  const reinitWrappers = () => {
    if (window._galleryGlobalMutationTimer) clearTimeout(window._galleryGlobalMutationTimer);
    window._galleryGlobalMutationTimer = setTimeout(() => {
      try {
        const wrappers = Array.from(document.querySelectorAll('.product-gallery__thumbnail-list-wrapper--list'));
        wrappers.forEach(w => {
          setupGalleryThumbnailNavSimple(w);
        });
      } catch (e) {
        console.error('gallery global reinit error', e);
      }
    }, 80);
  };

  const mo = new MutationObserver((records) => {
    let found = false;
    for (const r of records) {
      if (r.addedNodes && r.addedNodes.length) {
        for (const n of r.addedNodes) {
          if (n.nodeType === 1 &&
            (n.classList && (n.classList.contains('product-gallery') || n.classList.contains('product-gallery__thumbnail-list-wrapper--list') || n.querySelector?.('.product-gallery__thumbnail-list-wrapper--list')))) {
            found = true;
            break;
          }
        }
      }
      if (found) break;
      if (r.type === 'attributes' && r.target && r.target.classList && r.target.classList.contains('product-gallery')) {
        found = true;
        break;
      }
    }
    if (found) reinitWrappers();
  });

  mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'id', 'data-section-id', 'data-section'] });
  window._galleryGlobalMutationObserver = mo;
}

window.galleryInspector = window.galleryInspector || {};
window.galleryInspector.reportAllSimple = function () {
  const wrappers = Array.from(document.querySelectorAll('.product-gallery__thumbnail-list-wrapper--list'));
  return wrappers.map((w, i) => {
    const pd = _findPageDots(w);
    const items = pd ? (pd.items ?? Array.from(pd.children)) : [];
    const visible = items.filter(el => el && el.offsetParent !== null && !el.hidden);
    const selected = pd ? (pd.selectedIndex ?? items.findIndex(b => b.getAttribute && b.getAttribute('aria-current') === 'true')) : -1;
    console.group(`gallery[${i}]`);
    console.groupEnd();
    return { wrapper: w, totalItems: items.length, visibleCount: visible.length, selectedIndex: selected };
  });
};

if (!window._galleryThumbResizeBound) {
  window._galleryThumbResizeBound = true;
  window.addEventListener('resize', () => {
    if (window._galleryResizeTimer) clearTimeout(window._galleryResizeTimer);
    window._galleryResizeTimer = setTimeout(() => {
      const wrappers = Array.from(document.querySelectorAll('.product-gallery__thumbnail-list-wrapper--list'));
      wrappers.forEach(w => {
        delete w.dataset.fixedWrapperHeight;
        delete w.dataset.fixedPageDotsMaxHeight;
        delete w.dataset.fixedVisibleCount;
        updateThumbnailListMaxHeight(w);
      });
    }, 100);
  });
}


if (!window._galleryNavDelegated) {
  window._galleryNavDelegated = true;

  document.addEventListener('click', (event) => {
    try {
      const path = event.composedPath ? event.composedPath() : [];
      const thumbBtn = path.find(node => node && node.nodeType === 1 && node.matches?.('.product-gallery__thumbnail'));
      if (thumbBtn) {
        const productGallery = path.find(node => node && node.nodeType === 1 && node.matches?.('.product-gallery')) || thumbBtn.closest?.('.product-gallery');
        const wrapper = (productGallery && (productGallery.querySelector('.product-gallery__thumbnail-list-wrapper') || productGallery.querySelector('scroll-shadow.product-gallery__thumbnail-list-wrapper'))) || thumbBtn.closest?.('.product-gallery__thumbnail-list-wrapper') || null;
        if (wrapper) {
          const pageDots = _findPageDots(wrapper);
          if (pageDots) {
            const items = pageDots.items ?? Array.from(pageDots.children);
            const realIndex = items.indexOf(thumbBtn);
            if (realIndex >= 0 && typeof pageDots.select === 'function') {
              pageDots.select(realIndex);
            } else {
              items.forEach(item => {
                if (item && item.setAttribute) {
                  item.setAttribute('aria-current', item === thumbBtn ? 'true' : 'false');
                }
              });
            }
            updateThumbnailListMaxHeight(wrapper);
            updateThumbNavState(wrapper);
          }
        }
      }

      const btn = path.find(node => node && node.nodeType === 1 && (node.matches?.('.product-gallery__thumbnail-nav--up') || node.matches?.('.product-gallery__thumbnail-nav--down')));
      if (!btn) return;

      const productGallery = path.find(node => node && node.nodeType === 1 && node.matches?.('.product-gallery')) || btn.closest?.('.product-gallery');
      const wrapper = (productGallery && (productGallery.querySelector('.product-gallery__thumbnail-list-wrapper') || productGallery.querySelector('scroll-shadow.product-gallery__thumbnail-list-wrapper'))) || btn.closest?.('.product-gallery__thumbnail-list-wrapper') || null;
      if (!wrapper) return;

      const pageDots = _findPageDots(wrapper);
      if (!pageDots) return;

      const items = pageDots.items ?? Array.from(pageDots.children);
      const visible = items.filter(el => !!el && el.offsetParent !== null && !el.hidden);
      if (!visible.length) return;

      const scrollStep = getThumbnailsScrollStep(pageDots);
      const isUp = btn.matches('.product-gallery__thumbnail-nav--up');
      const currentScrollTop = pageDots.scrollTop || 0;
      const nextTop = isUp
        ? Math.max(0, currentScrollTop - scrollStep)
        : Math.min(pageDots.scrollHeight - pageDots.clientHeight, currentScrollTop + scrollStep);

      if (!isUp) {
      }

      if (event && typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      if (typeof pageDots.scrollTo === 'function') {
        pageDots.scrollTo({ top: nextTop, behavior: 'smooth' });
      } else {
        pageDots.scrollTop = nextTop;
      }

      updateThumbnailListMaxHeight(wrapper);
      updateThumbNavState(wrapper);
      window.setTimeout(() => updateThumbNavState(wrapper), 180);
    } catch (err) {
      console.error('gallery delegate error', err);
    }
  }, true);
}
